import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { AppDispatch } from '../store/store';
import SubmissionDetail from '../components/SubmissionDetail/SubmissionDetail';
import { Submission } from '../store/submission/types';
import {
  selectCurrentRole,
  selectCurrentUser,
  selectKeywordState,
  selectAssignmentState,
  selectRoleRelations,
  selectSubmissionState,
  selectTrackRoles,
  selectCanViewAuthors,
  selectCurrentTrack,
} from '../store/selectors';
import { Answer, Answerable, Form, Question } from '../store/form/types';
import { FormTypeEnum } from '../store/form/types.d';
import Loading from '../components/Loading/Loading';
import { useParams } from 'react-router-dom';
import { getRouteByName } from '../router/routes';
import { fillRoutePath } from '../helpers/path';
import history from '../store/history';
import { getFormsData } from '../helpers/form';
import submissionSlice from '../store/submission/slice';
import { AppState } from '../store/state';
import { Review } from '../store/review/types';
import assignmentSlice from '../store/review/slice';
import { Role } from '../store/conference/types';
import { User } from '../store/user/types';
import { AssignmentPermissionManager, SubmissionPermissionManager } from '../helpers/permissions';
import SimpleDialog from '../components/dialogs/SimpleDialog/SimpleDialog';
import { MultiSelectCheckbox } from '../components/ui';
import SendEmailDialog from '../components/dialogs/SendEmailDialog/SendEmailDialog';
import { useTranslation } from 'react-i18next';
import { getAssignmentsForSubmission } from '../helpers/assignments';
import { getUserAndRoleDisplayName } from '../helpers/users';
import PaperStatusChangeDialog from '../components/dialogs/PaperStatusChangeDialog/PaperStatusChangeDialog';
import PaperStatusChangeDialogMobile from '../components/dialogs/PaperStatusChangeDialogMobile/PaperStatusChangeDialogMobile';
import { useIsMobile } from '../helpers/window';

interface Props {
  loading: boolean;
  loadingReviews: boolean;
  submissionsById: { [key: string]: Submission };
  assignments: Review[];
  keywordState: KeywordState;
  user: User;
  roleId?: number;
  getSubmissionsAction: () => void;
  roleById: { [key: string]: Role };
  emailRoleIds: number[];
  canViewAuthors: boolean;
  getAssignmentDetailsAction: (submissionId: number) => void;
  authorConfirmation: boolean;
}

const SubmissionDetailPage = ({
  loading,
  loadingReviews,
  submissionsById,
  assignments,
  keywordState,
  user,
  roleId,
  getSubmissionsAction,
  roleById,
  emailRoleIds,
  canViewAuthors,
  getAssignmentDetailsAction,
  authorConfirmation,
}: Props) => {
  const { t, i18n } = useTranslation();
  const params: any = useParams();
  const { id: external_id } = params;
  const isMobile = useIsMobile();

  // Given id must match with external_id field
  const findSubmission = Object.values(submissionsById).find((submission) => submission.external_id == external_id);

  const userRole = roleById[roleId ?? 0];
  const isChair = userRole.type == 'chair';

  // Send email related states
  const [isRecipientOpen, setIsRecipientOpen] = React.useState<boolean>(false);
  const [isEmailOpen, setIsEmailOpen] = React.useState<boolean>(false);
  const [value, setValue] = React.useState<string[]>([]);
  const [dialog, setDialog] = React.useState<JSX.Element | undefined>(undefined);

  useEffect(() => {
    if (isChair) {
      if (!Object.values(submissionsById).length) {
        getSubmissionsAction();
      }
    }
  }, []);

  useEffect(() => {
    if (findSubmission?.id && isChair) {
      // Fetch assignments data for given submission
      getAssignmentDetailsAction(findSubmission.id);
    }
  }, [findSubmission?.id]);

  const getRecipientsOptions = (reviews: Review[]): { id: string; label: string }[] => {
    const options: { id: string; label: string }[] = [];

    const authorRole = Object.values(roleById).find((role) => role.type == 'author');
    if (authorRole && emailRoleIds.includes(authorRole.id)) {
      options.push({
        id: 'authors',
        label: t('Authors'),
      });
      options.push({
        id: 'contact-authors',
        label: t('Managing authors'),
      });
    }

    reviews
      .filter((review) => emailRoleIds.includes(review.role_id))
      .filter((review) => review.person.id != user.person.id)
      .sort((reviewA, reviewB) => {
        return reviewA.role_id - reviewB.role_id || reviewA.external_id - reviewB.external_id; // ORDER BY role_id, external_id ASC
      })
      .forEach((review) => {
        const role = roleById[review.role_id];
        options.push({
          id: review.person_role_id.toString(),
          label: getUserAndRoleDisplayName(userRole, role, review.person.full_name, false, t, review.external_id)
            .join(' ')
            .trim(),
        });
      });
    return options;
  };

  const optionsToIds = (submission: Submission): number[] => {
    const ids: Set<number> = new Set<number>();
    value.forEach((option) => {
      if (option === 'contact-authors') {
        submission.authors.filter((author) => author.is_primary).forEach((author) => ids.add(author.person_role_id));
      } else if (option === 'authors') {
        submission.authors.forEach((author) => ids.add(author.person_role_id));
      } else {
        ids.add(parseInt(option));
      }
    });
    return Array.from(ids);
  };

  const handleCloseRecipientModal = () => {
    setIsRecipientOpen(false);
    setValue([]);
  };

  if (loading || !roleId) {
    return <Loading />;
  } else if (!findSubmission) {
    return <div>{t('Submission #{{id}} not found', { id: external_id })}</div>;
  }

  const authorRole = Object.values(roleById).find((role) => role.type == 'author');
  if (!authorRole) throw Error('Author role does not exist.');

  // Get Submission type forms
  const submissionFormsData = [findSubmission].map((submission) => {
    const isOwner = new SubmissionPermissionManager(submission).isAuthor(user.person.id, roleById[roleId]);
    return getFormsData(
      FormTypeEnum.Submission,
      ['read', 'write'],
      roleId,
      isOwner ? null : authorRole.id,
      user.person.id,
      submission,
    );
  });
  const submission = submissionFormsData[0].answerable as Submission;

  // Get Review type forms — exclude reviewer-to-reviewer assignments
  const submissionAssignments = getAssignmentsForSubmission(submission.id, assignments);
  const reviewFormsData: {
    answerable: Answerable;
    formsData: { form: Form; questions: Question[]; answers: Answer[] }[];
  }[] = submissionAssignments.map((assignment) => {
    const isOwner = new AssignmentPermissionManager(assignment).isAuthor(user.person.id, roleId);
    return getFormsData(
      FormTypeEnum.Review,
      ['read', 'write'],
      roleId,
      isOwner ? null : assignment.role_id,
      user.person.id,
      assignment,
    );
  });

  // Check own review status (for track 86, PC / Emergency Review roles)
  const showReviewStatusBanner =
    userRole.track_id === 86 &&
    userRole.type === 'reviewer' &&
    (userRole.description === 'PC' || userRole.description === 'Emergency Review');
  const ownReviewCompleted =
    showReviewStatusBanner &&
    reviewFormsData.some((data) => {
      const review = data.answerable as Review;
      return (
        new AssignmentPermissionManager(review).isAuthor(user.person.id, roleId) &&
        review.validation_status === 'Complete'
      );
    });

  // Get permissions
  const role = roleById[roleId];
  const spm = new SubmissionPermissionManager(submission);
  const forms = submissionFormsData[0].formsData.map((value) => value.form);
  const canEdit = spm.canEdit(user, role, forms);
  const canViewDiscussion = spm.canViewDiscussion(user, role);
  const canEditPaperStatus = spm.canEditPaperStatus(role);
  const canViewPaperStatus = spm.canViewPaperStatus(user, role, forms);
  const isPendingAuthor = authorConfirmation && spm.isPendingAuthor(user.person.id, role);

  return isPendingAuthor ? (
    <div>{t('Forbidden')}</div>
  ) : (
    <>
      <SubmissionDetail
        isLoadingReviews={loadingReviews}
        submission={submission}
        keywordState={keywordState}
        roleById={roleById}
        submissionFormsData={submissionFormsData}
        reviewFormsData={reviewFormsData}
        onEditClick={
          canEdit
            ? () => history.push(fillRoutePath(getRouteByName('RouteEditSubmission').path, { id: external_id }))
            : undefined
        }
        onSendEmailClick={emailRoleIds.length > 0 ? () => setIsRecipientOpen(true) : undefined}
        onViewDiscussionClick={
          canViewDiscussion
            ? () => history.push(fillRoutePath(getRouteByName('RouteDiscussions').path, { submissionId: external_id }))
            : undefined
        }
        onPaperStatusEdit={
          canEditPaperStatus
            ? () => {
                setDialog(
                  !isMobile ? (
                    <PaperStatusChangeDialog
                      initialOption={submission.paper_status_id.toString()}
                      submissionIds={[submission.id]}
                      open={true}
                      handleClose={() => setDialog(undefined)}
                      role={role}
                    />
                  ) : (
                    <PaperStatusChangeDialogMobile
                      initialOption={submission.paper_status_id.toString()}
                      submissionIds={[submission.id]}
                      open={true}
                      handleClose={() => setDialog(undefined)}
                      role={role}
                    />
                  ),
                );
              }
            : undefined
        }
        canViewPaperStatus={canViewPaperStatus}
        canViewAuthors={canViewAuthors}
        currentRoleId={roleId}
        personId={user.person.id}
        hideOtherPcReviews={showReviewStatusBanner && !ownReviewCompleted}
        onMove={(rowId) => {
          history.replace(
            fillRoutePath(getRouteByName('RouteDetailSubmission').path, {
              id: submissionsById[rowId].external_id.toString(),
            }),
          );
        }}
      />
      {dialog}
      {isRecipientOpen && (
        <SimpleDialog
          title={t('Send email')}
          closeOnOk={false}
          handleClose={handleCloseRecipientModal}
          handleOk={() => {
            setIsRecipientOpen(false);
            setIsEmailOpen(true);
          }}
          disableOkButton={!value.length}
        >
          <MultiSelectCheckbox
            className="flex flex-col space-y-2"
            label={t('Choose recipients')}
            required
            value={value}
            options={getRecipientsOptions(submissionAssignments)}
            onChange={setValue}
          />
        </SimpleDialog>
      )}
      {isEmailOpen && (
        <SendEmailDialog
          title={t('Send email')}
          modelIds={[submission.id]}
          recipientType="submission:ids"
          recipientIds={optionsToIds(submission)}
          handleClose={() => {
            handleCloseRecipientModal();
            setIsEmailOpen(false);
          }}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectSubmissionState(state).loading,
  loadingReviews: selectAssignmentState(state).loading,
  submissionsById: selectSubmissionState(state).submissionsById,
  keywordById: selectKeywordState(state).keywordById,
  assignments: selectAssignmentState(state).assignments,
  keywordState: selectKeywordState(state),
  user: selectCurrentUser(state),
  roleId: selectCurrentRole(state)?.id,
  roleById: selectTrackRoles(state),
  emailRoleIds: selectRoleRelations(state, 'send-email'),
  canViewAuthors: selectCanViewAuthors(state),
  authorConfirmation: selectCurrentTrack(state)?.author_confirmation ?? false,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getSubmissionsAction: () => dispatch(submissionSlice.actions.GET_SUBMISSIONS()),
  getAssignmentDetailsAction: (submissionId: number) =>
    dispatch(assignmentSlice.actions.GET_ASSIGNMENT_DETAILS(submissionId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionDetailPage);
